/**
 * ModalManager - General-purpose modal system
 * Replaces browser alert() and confirm() with beautiful modals
 */

import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('ModalManager');

export type ModalType = 'alert' | 'confirm' | 'custom';

export interface ModalOptions {
  title: string;
  message?: string;
  type?: ModalType;
  confirmText?: string;
  cancelText?: string;
  icon?: string;
  customContent?: string; // HTML content for custom modals
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  showCancel?: boolean;
  lockClose?: boolean; // Prevent closing by overlay click or Escape
  preventAutoClose?: boolean; // Don't auto-close after onConfirm (for games, etc.)
  theme?: 'pacman' | 'normal'; // Theme style: pacman (retro gaming) or normal (clean modern)
}

class ModalManager {
  private modalContainer: HTMLElement | null = null;
  private activeModal: HTMLElement | null = null;
  private resolveCallback: ((value: boolean) => void) | null = null;
  // FIXED: Track escape handler at class level to ensure cleanup on all close paths
  private currentEscapeHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    this.initializeContainer();
  }

  /**
   * Initialize modal container (singleton)
   */
  private initializeContainer() {
    const existingContainer = document.getElementById('modal-manager-container');
    if (existingContainer) {
      this.modalContainer = existingContainer;
      return;
    }

    const container = document.createElement('div');
    container.id = 'modal-manager-container';
    container.innerHTML = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100000;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
          padding: 20px;
        }

        .modal-overlay.show {
          opacity: 1;
          visibility: visible;
        }

        .modal-content {
          background: var(--bg-secondary, #ffffff);
          border: 1px solid var(--glass-border, #e0e0e0);
          box-shadow: var(--shadow-lg, 0 10px 40px rgba(0, 0, 0, 0.2));
          border-radius: var(--radius-xl, 16px);
          max-width: 600px;
          width: 100%;
          max-height: 95vh;
          overflow: hidden;
          transform: scale(0.95) translateY(10px);
          transition: transform 0.3s ease;
        }

        .modal-overlay.show .modal-content {
          transform: scale(1) translateY(0);
        }

        .modal-header {
          padding: 24px 24px 0 24px;
          text-align: center;
        }

        .modal-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }

        .modal-title {
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 16px;
          color: var(--text-primary, #1a1a1a);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .modal-body {
          padding: 0 24px 24px 24px;
          text-align: center;
          max-height: calc(95vh - 120px);
          overflow-y: auto;
          color: var(--text-secondary, #666);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .modal-message {
          font-size: 15px;
          line-height: 1.6;
          margin-bottom: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .modal-custom-content {
          text-align: left;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .modal-custom-content canvas {
          max-width: 100%;
          height: auto;
          display: block;
        }

        .modal-custom-content p,
        .modal-custom-content div {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-top: 8px;
        }

        .modal-btn {
          padding: 12px 24px;
          border: none;
          border-radius: var(--radius-full, 20px);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }

        .modal-btn-primary {
          background: linear-gradient(135deg, var(--red-primary, #FF3B30) 0%, #FF6B6B 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(255, 59, 48, 0.3);
        }

        .modal-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 59, 48, 0.4);
        }

        .modal-btn-primary:active {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(255, 59, 48, 0.3);
        }

        .modal-btn-secondary {
          background: var(--bg-tertiary, #f0f0f0);
          color: var(--text-primary, #333);
          border: none;
        }

        .modal-btn-secondary:hover {
          background: var(--bg-tertiary, #e0e0e0);
          filter: brightness(0.95);
        }

        .modal-btn-secondary:active {
          filter: brightness(0.9);
        }

        .modal-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .loading {
          border: 3px solid rgba(255, 59, 48, 0.3);
          border-left-color: var(--red-primary, #FF3B30);
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 1s linear infinite;
          display: inline-block;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Normal Theme (default) - Uses CSS variables for theming */
        .modal-theme-normal .modal-content {
          background: var(--bg-secondary, #ffffff);
          border: 1px solid var(--glass-border, #e0e0e0);
          box-shadow: var(--shadow-lg, 0 10px 40px rgba(0, 0, 0, 0.2));
        }

        .modal-theme-normal .modal-title {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: var(--text-primary, #1a1a1a);
          font-size: 22px;
        }

        .modal-theme-normal .modal-body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: var(--text-secondary, #666);
        }

        .modal-theme-normal .modal-message {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 15px;
          line-height: 1.6;
        }

        .modal-theme-normal .modal-custom-content,
        .modal-theme-normal .modal-custom-content p,
        .modal-theme-normal .modal-custom-content div {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: var(--text-primary, #333);
        }

        .modal-theme-normal .modal-btn {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* Pac-Man Theme (retro gaming) */
        .modal-theme-pacman .modal-content {
          background: #111;
          border: 4px solid #2244cc;
          box-shadow: 0 0 30px rgba(255, 204, 0, 0.4), 0 0 60px rgba(34, 68, 204, 0.3);
        }

        .modal-theme-pacman .modal-icon {
          text-shadow: 2px 2px 0 rgba(0, 0, 0, 0.3);
        }

        .modal-theme-pacman .modal-title {
          font-family: 'Press Start 2P', cursive;
          color: #ffcc00;
          font-size: 18px;
          letter-spacing: 2px;
          text-shadow: 3px 3px 0 rgba(0, 0, 0, 0.5);
        }

        .modal-theme-pacman .modal-body {
          font-family: 'Press Start 2P', cursive;
          color: white;
          padding: 0 24px 24px 24px;
        }

        .modal-theme-pacman .modal-message {
          font-family: 'Press Start 2P', cursive;
          font-size: 11px;
          color: #fff;
          line-height: 1.8;
        }

        .modal-theme-pacman .modal-custom-content,
        .modal-theme-pacman .modal-custom-content p,
        .modal-theme-pacman .modal-custom-content div {
          font-family: 'Press Start 2P', cursive;
        }

        .modal-theme-pacman .modal-btn {
          font-family: 'Press Start 2P', cursive;
          font-size: 12px;
          padding: 12px 24px;
          text-transform: uppercase;
        }

        .modal-theme-pacman .modal-btn-primary {
          background: #ffcc00;
          color: #000;
          box-shadow: 0 4px 0 #b38f00;
        }

        .modal-theme-pacman .modal-btn-primary:hover {
          background: #ffd700;
          transform: none;
          box-shadow: 0 4px 0 #b38f00;
        }

        .modal-theme-pacman .modal-btn-primary:active {
          transform: translateY(4px);
          box-shadow: 0 0 0 #b38f00;
        }

        .modal-theme-pacman .modal-btn-secondary {
          background: #2244cc;
          color: white;
          border: none;
          box-shadow: 0 4px 0 #1a3399;
        }

        .modal-theme-pacman .modal-btn-secondary:hover {
          background: #2855ff;
          box-shadow: 0 4px 0 #1a3399;
        }

        .modal-theme-pacman .modal-btn-secondary:active {
          transform: translateY(4px);
          box-shadow: 0 0 0 #1a3399;
        }

        .modal-theme-pacman .loading {
          border: 3px solid rgba(255, 204, 0, 0.3);
          border-left-color: #ffcc00;
        }
      </style>
    `;

    document.body.appendChild(container);
    this.modalContainer = container;
  }

  /**
   * Show alert modal (replaces window.alert)
   */
  alert(message: string, title: string = 'Notice', icon: string = 'ℹ️'): Promise<void> {
    return new Promise((resolve) => {
      this.show({
        title,
        message,
        icon,
        type: 'alert',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => resolve()
      });
    });
  }

  /**
   * Show confirm modal (replaces window.confirm)
   */
  confirm(
    message: string,
    title: string = 'Confirm',
    icon: string = '❓',
    confirmText: string = 'Confirm',
    cancelText: string = 'Cancel'
  ): Promise<boolean> {
    return new Promise((resolve) => {
      this.resolveCallback = resolve;
      this.show({
        title,
        message,
        icon,
        type: 'confirm',
        confirmText,
        cancelText,
        showCancel: true,
        onConfirm: () => {
          this.resolveCallback?.(true);
          this.resolveCallback = null;
        },
        onCancel: () => {
          this.resolveCallback?.(false);
          this.resolveCallback = null;
        }
      });
    });
  }

  /**
   * Show custom modal
   */
  custom(options: ModalOptions): void {
    logger.debug('[ModalManager] custom() called with title:', options.title);
    this.show(options);
  }

  /**
   * Show modal
   */
  private show(options: ModalOptions) {
    // Close any existing modal
    this.close();

    const {
      title,
      message,
      icon = 'ℹ️',
      confirmText = 'OK',
      cancelText = 'Cancel',
      customContent = '',
      onConfirm,
      onCancel,
      showCancel = false,
      lockClose = false,
      preventAutoClose = false,
      theme = 'normal' // Default to normal theme
    } = options;

    // Create modal
    const modal = document.createElement('div');
    modal.className = `modal-overlay modal-theme-${theme}`;
    modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          ${icon ? `<div class="modal-icon">${icon}</div>` : ''}
          <div class="modal-title">${title}</div>
        </div>
        <div class="modal-body">
          ${message ? `<div class="modal-message">${message}</div>` : ''}
          ${customContent ? `<div class="modal-custom-content">${customContent}</div>` : ''}
          ${confirmText || showCancel ? `
            <div class="modal-actions">
              ${showCancel ? `<button class="modal-btn modal-btn-secondary" data-action="cancel">${cancelText}</button>` : ''}
              ${confirmText ? `<button class="modal-btn modal-btn-primary" data-action="confirm">${confirmText}</button>` : ''}
            </div>
          ` : ''}
        </div>
      </div>
    `;

    // Add to container (with fallback reinit if somehow null)
    if (!this.modalContainer) {
      logger.warn('Modal container was null, reinitializing...');
      this.initializeContainer();
    }

    if (this.modalContainer) {
      this.modalContainer.appendChild(modal);
    } else {
      logger.error('CRITICAL: Modal container still null after reinit - modal will not show');
    }
    this.activeModal = modal;

    // Show with animation
    setTimeout(() => modal.classList.add('show'), 10);

    // Setup button handlers
    const confirmBtn = modal.querySelector('[data-action="confirm"]') as HTMLButtonElement;
    const cancelBtn = modal.querySelector('[data-action="cancel"]') as HTMLButtonElement;

    confirmBtn?.addEventListener('click', async () => {
      if (onConfirm) {
        try {
          confirmBtn.disabled = true;
          confirmBtn.innerHTML = '<div class="loading"></div>';
          await onConfirm();
        } catch (error) {
          logger.error('Modal confirm action failed:', error);
          // Don't close on error - let the callback handle it
          confirmBtn.disabled = false;
          confirmBtn.textContent = confirmText;
          return;
        }
      }
      // Only auto-close if not prevented
      if (!preventAutoClose) {
        this.close();
      }
    });

    cancelBtn?.addEventListener('click', () => {
      if (onCancel) onCancel();
      this.close();
    });

    // Close on overlay click (unless locked)
    modal.addEventListener('click', (e) => {
      if (!lockClose && e.target === modal) {
        if (onCancel) onCancel();
        this.close();
      }
    });

    // FIXED: Close on Escape key (unless locked)
    // Store handler reference for cleanup in close()
    this.currentEscapeHandler = (e: KeyboardEvent) => {
      if (!lockClose && e.key === 'Escape') {
        if (onCancel) onCancel();
        this.close();
      }
    };
    document.addEventListener('keydown', this.currentEscapeHandler);
  }

  /**
   * Close active modal
   * FIXED: Now properly removes escape handler to prevent memory leaks
   */
  close() {
    if (!this.activeModal) return;

    // FIXED: Remove escape handler to prevent memory leaks
    if (this.currentEscapeHandler) {
      document.removeEventListener('keydown', this.currentEscapeHandler);
      this.currentEscapeHandler = null;
    }

    // Store reference to the modal being closed (fix race condition!)
    const modalToClose = this.activeModal;
    this.activeModal = null; // Clear activeModal immediately

    modalToClose.classList.remove('show');
    setTimeout(() => {
      modalToClose.remove(); // Remove the SPECIFIC modal, not this.activeModal
    }, 300);
  }

  /**
   * Force close modal (public method for external use)
   */
  forceClose() {
    this.close();
  }
}

// Export singleton instance
export const modalManager = new ModalManager();

// Export convenience functions
export const showAlert = (message: string, title?: string, icon?: string) =>
  modalManager.alert(message, title, icon);

export const showConfirm = (message: string, title?: string, icon?: string, confirmText?: string, cancelText?: string) =>
  modalManager.confirm(message, title, icon, confirmText, cancelText);

export const showCustomModal = (options: ModalOptions) =>
  modalManager.custom(options);

export const closeModal = () =>
  modalManager.forceClose();
